import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, Search, X, ChevronDown, ArrowRight, ExternalLink, Mail } from "lucide-react";
import { SEO } from "@/components/site/SEO";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { StickyCta } from "@/components/site/StickyCta";
import { Section, Eyebrow, Heading } from "@/components/site/pageui";
import { AuthorByline } from "@/components/site/AuthorByline";
import { JsonLd, faqPageSchema, breadcrumbSchema } from "@/components/site/Schema";
import { FAQS, FAQ_CATEGORIES } from "@/lib/faqs";

const FaqItem = ({ item, isOpen, onToggle }) => (
  <div className="border-b border-taupe/50" data-testid={`faq-item-${item.id}`}>
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      data-testid={`faq-toggle-${item.id}`}
      className="w-full flex items-start justify-between gap-4 py-5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
    >
      <span className="font-serif text-lg md:text-xl text-legacy leading-snug">{item.q}</span>
      <ChevronDown size={20} className={`shrink-0 mt-1 text-bronze transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
    </button>
    {isOpen && (
      <div className="pb-6 -mt-1 max-w-3xl">
        <p className="text-slate text-[0.95rem] leading-relaxed">{item.a}</p>
        {item.link && item.link.to && (
          <Link to={item.link.to} className="mt-3 inline-flex items-center gap-1.5 text-bronze font-semibold text-sm hover:text-gold transition-colors">
            {item.link.label}<ArrowRight size={15} />
          </Link>
        )}
        {item.link && item.link.href && (
          <a href={item.link.href} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-bronze font-semibold text-sm hover:text-gold transition-colors">
            {item.link.label}<ExternalLink size={14} />
          </a>
        )}
      </div>
    )}
  </div>
);

export default function FAQs() {
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState(null);
  const [openIds, setOpenIds] = useState(() => new Set());

  const toggle = (id) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FAQS.filter((f) => {
      const matchesCat = !activeCat || f.cat === activeCat;
      const matchesText = !q || f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q);
      return matchesCat && matchesText;
    });
  }, [query, activeCat]);

  const grouped = useMemo(() => {
    return FAQ_CATEGORIES.map((cat) => ({
      cat,
      items: filtered.filter((f) => f.cat === cat),
    })).filter((g) => g.items.length > 0);
  }, [filtered]);

  const hasFilter = query.trim() || activeCat;
  const clearAll = () => { setQuery(""); setActiveCat(null); };

  return (
    <div className="min-h-screen bg-ivory" data-testid="faqs-page">
      <SEO
        title="Infinite Banking and Financial Planning FAQs | Legacy Life"
        description="Find straightforward answers about Infinite Banking, policy loans, whole life insurance, debt payoff, retirement income, long-term care, annuities, and family legacy planning."
      />
      <JsonLd id="ld-faqpage" data={faqPageSchema(FAQS)} />
      <JsonLd id="ld-faq-breadcrumb" data={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "FAQs", path: "/faqs" }])} />
      <Header />
      <main>
        {/* Hero */}
        <section data-testid="hero-section" className="relative overflow-hidden bg-[#E9DFCF] pt-[120px] md:pt-[150px] pb-16 md:pb-20 border-b border-bronze/20">
          {/* Linen / paper texture */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-50 md:opacity-70 bg-[repeating-linear-gradient(0deg,rgba(120,100,70,0.05)_0px,rgba(120,100,70,0.05)_1px,transparent_1px,transparent_4px),repeating-linear-gradient(90deg,rgba(120,100,70,0.04)_0px,rgba(120,100,70,0.04)_1px,transparent_1px,transparent_4px)]" />
          {/* Faint grid overlay */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 grid-lines opacity-[0.16]" />
          {/* Soft Heritage Gold radial glow, upper-right */}
          <div aria-hidden="true" className="pointer-events-none absolute -top-24 right-[-4rem] h-[34rem] w-[34rem] rounded-full bg-[radial-gradient(circle,rgba(199,161,90,0.28),transparent_65%)]" />
          {/* Low-opacity decorative rings (desktop) */}
          <div aria-hidden="true" className="pointer-events-none absolute top-1/2 right-[-9rem] hidden md:block h-[30rem] w-[30rem] -translate-y-1/2 rounded-full border border-gold/25" />
          <div aria-hidden="true" className="pointer-events-none absolute top-1/2 right-[1rem] hidden md:block h-[20rem] w-[20rem] -translate-y-1/2 rounded-full border border-bronze/20" />
          {/* Faint bronze edge vignette */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 shadow-[inset_0_0_150px_rgba(120,90,50,0.16)]" />
          {/* Subtle bottom divider */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
          <div className="container-lr relative z-10 max-w-3xl">
            <Eyebrow>Clear Answers to Common Questions</Eyebrow>
            <h1 className="mt-6 font-serif font-semibold tracking-display text-legacy text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Financial Concepts Should Be <span className="accent-word">Understandable.</span>
            </h1>
            <p className="mt-7 text-slate text-base md:text-lg leading-relaxed">
              Explore straightforward answers to common questions about Infinite Banking, whole life insurance, policy loans, retirement income, long-term care, family legacy planning, and working with Legacy Life &amp; Retirement.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/resources#newsletter" data-testid="faq-hero-newsletter-cta" className="btn-base btn-primary"><Mail size={18} />Join Our Newsletter</Link>
              <Link to="/strategy-call" className="btn-base btn-outline-dark"><CalendarCheck size={18} />Book Your Free Strategy Call</Link>
            </div>
            <AuthorByline className="mt-8" />
          </div>
        </section>

        {/* Search and filters */}
        <Section id="search" className="bg-[#EFE8D9] !py-14">
          <div className="max-w-3xl">
            <label htmlFor="faq-search" className="sr-only">Search questions</label>
            <div className="relative">
              <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate/60 pointer-events-none" />
              <input
                id="faq-search"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search questions"
                data-testid="faq-search-input"
                className="w-full rounded-button border border-taupe/60 bg-white pl-12 pr-12 py-4 text-base text-legacy placeholder:text-slate/60 focus:outline-none focus:border-gold"
              />
              {query && (
                <button type="button" onClick={() => setQuery("")} aria-label="Clear search" data-testid="faq-clear-search" className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-full text-slate hover:bg-taupe/20 transition-colors">
                  <X size={18} />
                </button>
              )}
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2.5" role="group" aria-label="Filter FAQs by category">
            {FAQ_CATEGORIES.map((cat) => {
              const active = activeCat === cat;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setActiveCat(active ? null : cat)}
                  aria-pressed={active}
                  data-testid={`faq-filter-${cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`rounded-xl border px-4 py-2.5 min-h-[2.6rem] text-[0.82rem] font-medium outline-none transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold ${
                    active
                      ? "border-gold bg-legacy text-ivory shadow-[0_8px_20px_-12px_rgba(17,17,17,0.55)]"
                      : "border-taupe/60 bg-white text-slate shadow-[0_1px_2px_rgba(17,17,17,0.04)] hover:-translate-y-0.5 hover:border-gold hover:text-legacy hover:shadow-[0_10px_22px_-14px_rgba(17,17,17,0.4)]"
                  }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>

          <div className="mt-5 flex items-center gap-4 text-sm text-slate">
            <span data-testid="faq-result-count">{filtered.length} {filtered.length === 1 ? "question" : "questions"}</span>
            {hasFilter && (
              <button type="button" onClick={clearAll} data-testid="faq-clear-all" className="inline-flex items-center gap-1.5 text-bronze font-semibold hover:text-gold transition-colors">
                <X size={15} />Clear results
              </button>
            )}
          </div>
        </Section>

        {/* Results */}
        <Section id="questions">
          {grouped.length === 0 ? (
            <div className="max-w-xl mx-auto text-center py-16" data-testid="faq-empty-state">
              <span className="gold-rule mx-auto" />
              <h2 className="mt-6 font-serif text-2xl md:text-3xl text-legacy">No questions match your search.</h2>
              <p className="mt-4 text-slate text-base leading-relaxed">Try a different keyword or clear your filters. You can also bring your question directly to a free strategy call.</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <button type="button" onClick={clearAll} className="btn-base btn-outline-dark">Clear Results</button>
                <Link to="/strategy-call" className="btn-base btn-primary"><CalendarCheck size={18} />Book Your Free Strategy Call</Link>
              </div>
            </div>
          ) : (
            <div className="space-y-14">
              {grouped.map((g) => (
                <div key={g.cat} data-testid={`faq-group-${g.cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <div className="flex items-center gap-4">
                    <span className="gold-rule" />
                    <h2 className="eyebrow">{g.cat}</h2>
                  </div>
                  <div className="mt-5">
                    {g.items.map((item) => (
                      <FaqItem key={item.id} item={item} isOpen={openIds.has(item.id)} onToggle={() => toggle(item.id)} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Final CTA */}
        <Section id="cta" className="bg-[#EFE8D9]">
          <div className="relative rounded-card bg-legacy overflow-hidden px-8 py-16 md:px-16 md:py-20 text-center">
            <div className="pointer-events-none absolute -top-px left-1/2 -translate-x-1/2 h-px w-40 bg-gold" />
            <div className="relative max-w-2xl mx-auto">
              <p className="eyebrow text-gold">Still Have Questions?</p>
              <Heading light>Still Have Questions? Start With a Conversation.</Heading>
              <p className="mt-5 text-taupe text-base md:text-lg leading-relaxed">You do not need to understand every detail before speaking with Shawn. The first conversation is designed to understand your questions, priorities, and current level of financial education.</p>
              <Link to="/strategy-call" data-testid="faq-final-cta" className="btn-base btn-gold mt-9"><CalendarCheck size={18} />Book Your Free Strategy Call</Link>
            </div>
          </div>
        </Section>
      </main>
      <Footer />
      <StickyCta />
    </div>
  );
}
